// --------------------------------------------------------------
// Oracle Risk Lab — full-screen dashboard tab (performance-tuned).
//
// Perf rules (per Nov 2026 fix):
//  - Backtest JSON cached once at module scope; later tab visits reuse it.
//  - All chart components wrapped in React.memo so they only re-render
//    when their own props change (not when the live-status poll fires or
//    the user hovers one chart).
//  - 60 fan paths collapsed into ONE SVG path with "M..L..M..L.." joins.
//  - Charts below the fold render only when scrolled into view
//    (IntersectionObserver). No work until the user gets there.
//  - Live status polls into its own tiny component — never re-renders
//    the chart tree.
//  - Shadows/filters trimmed; glow kept only on status badge + fan median.
// --------------------------------------------------------------
import React, { useEffect, useMemo, useRef, useState, memo } from "react";
import { useNavigate } from "react-router-dom";
import { apiGet } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { OracleNav } from "@/pages/OracleTradesPage";

// ---------- palette ----------
const C = {
  cyan:   "#4de8ff",
  green:  "#45f0a6",
  amber:  "#ffc65c",
  red:    "#ff5c7a",
  pink:   "#ff5ad9",
  purple: "#9b8cff",
  muted:  "#8ea6c6",
  panel:  "rgba(10,18,40,0.88)",   // solid semi-transparent navy, no blur
  stroke: "rgba(77,232,255,0.35)",
};
export const RISK_COLOR = { green: C.green, amber: C.amber, red: C.red };

const money = (n, d = 0) =>
  (n == null || isNaN(n)) ? "—" :
    `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: d, minimumFractionDigits: d })}`;
const pctF = (frac, d = 1) => (frac == null || isNaN(frac)) ? "—" : `${(frac * 100).toFixed(d)}%`;
const signPct = (frac, d = 1) => (frac == null || isNaN(frac)) ? "—" : `${frac >= 0 ? "+" : ""}${(frac * 100).toFixed(d)}%`;
const num = (n, d = 2) => (n == null || isNaN(n)) ? "—" : Number(n).toFixed(d);

// Module-level cache for the 774KB backtest payload.
let _btCache = null;

// ---------- re-usable corner-bracket panel ----------
function Brackets() {
  const s = { position: "absolute", width: 10, height: 10, pointerEvents: "none" };
  return (
    <>
      <span style={{ ...s, top: 0,    left: 0,    borderTop: `1px solid ${C.cyan}`, borderLeft: `1px solid ${C.cyan}` }} />
      <span style={{ ...s, top: 0,    right: 0,   borderTop: `1px solid ${C.cyan}`, borderRight: `1px solid ${C.cyan}` }} />
      <span style={{ ...s, bottom: 0, left: 0,    borderBottom: `1px solid ${C.cyan}`, borderLeft: `1px solid ${C.cyan}` }} />
      <span style={{ ...s, bottom: 0, right: 0,   borderBottom: `1px solid ${C.cyan}`, borderRight: `1px solid ${C.cyan}` }} />
    </>
  );
}
function Panel({ title, right, children, pad = 4, "data-testid": tid, style }) {
  return (
    <section
      data-testid={tid}
      className="relative rounded-lg"
      style={{
        background: C.panel,
        border: `1px solid ${C.stroke}`,
        padding: `${pad * 4}px`,
        boxShadow: "0 6px 18px rgba(0,0,0,.4)",
        ...style,
      }}
    >
      <Brackets />
      {title && (
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="ora-h" style={{ fontSize: 13 }}>{title}</div>
          {right}
        </div>
      )}
      {children}
    </section>
  );
}

// ---------- Lazy wrapper: render children only once scrolled into view ----------
function Lazy({ children, minHeight = 220, rootMargin = "200px" }) {
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    if (seen || !ref.current || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting) { setSeen(true); io.disconnect(); }
    }, { rootMargin });
    io.observe(ref.current);
    return () => io.disconnect();
  }, [seen, rootMargin]);
  return (
    <div ref={ref} style={{ minHeight: seen ? undefined : minHeight }}>
      {seen ? children : null}
    </div>
  );
}

// ---------- Live-status subtree (polls on its own; isolated from charts) ----------
function LiveMeter({ label, value, unit, amber, red, status }) {
  const max = Math.max(red * 1.15, (value || 0) * 1.1, red + 1);
  const pct = Math.min(100, (Math.max(0, value) / max) * 100);
  const amberPct = Math.min(100, (amber / max) * 100);
  const redPct = Math.min(100, (red / max) * 100);
  const barColor = RISK_COLOR[status] || C.green;
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-baseline justify-between gap-2">
        <span style={{ fontSize: 10, color: C.muted, letterSpacing: ".18em", textTransform: "uppercase" }}>{label}</span>
        <span className="font-mono text-sm" style={{ color: barColor }}>
          {typeof value === "number" ? (unit === "%" ? value.toFixed(2) : Math.round(value)) : "0"}{unit}
        </span>
      </div>
      <div style={{ position: "relative", height: 8, borderRadius: 999, background: "rgba(255,255,255,.06)" }}>
        <div style={{ position: "absolute", top: 0, bottom: 0, left: `${amberPct}%`, right: `${100 - redPct}%`, background: `${C.amber}33` }} />
        <div style={{ position: "absolute", top: 0, bottom: 0, left: `${redPct}%`, right: 0, background: `${C.red}33` }} />
        <div style={{ position: "absolute", top: 0, bottom: 0, left: 0, width: `${pct}%`, borderRadius: 999, background: barColor }} />
        <div style={{ position: "absolute", top: -2, bottom: -2, left: `calc(${amberPct}% - 1px)`, width: 2, background: C.amber }} />
        <div style={{ position: "absolute", top: -2, bottom: -2, left: `calc(${redPct}% - 1px)`, width: 2, background: C.red }} />
      </div>
      <div className="flex items-center justify-between" style={{ fontSize: 9, color: C.muted }}>
        <span>0{unit}</span>
        <span style={{ color: C.amber }}>amber {unit === "%" ? amber.toFixed(1) : amber}{unit}</span>
        <span style={{ color: C.red }}>red {unit === "%" ? red.toFixed(1) : red}{unit}</span>
      </div>
    </div>
  );
}
const LiveSetupCard = memo(function LiveSetupCard({ p, livePnl }) {
  const m = p.metrics, c = RISK_COLOR[p.status];
  const pulse = p.status === "amber" || p.status === "red";
  return (
    <article data-testid={`risk-card-setup-${p.setup}`} className="rounded-lg p-4 flex flex-col gap-3"
             style={{ border: `1px solid ${c}66`, background: "rgba(5,14,30,.6)" }}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="ora-h" style={{ fontSize: 13 }}>Setup {p.setup}</div>
        <span className={pulse ? "ora-pulse ora-h" : "ora-h"}
              style={{ padding: "3px 10px", borderRadius: 999, background: `${c}22`, border: `1px solid ${c}`,
                color: c, fontSize: 11, letterSpacing: ".2em",
                boxShadow: `0 0 10px ${c}80`,
                textShadow: `0 0 6px ${c}aa` }}>
          ● {p.status.toUpperCase()}
        </span>
      </div>
      <div style={{ fontSize: 12, color: C.muted }}>{p.meaning}</div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
        <div><div style={{ color: C.muted, fontSize: 10, letterSpacing: ".2em" }}>EQUITY</div><div className="font-mono text-sm">{money(m.current_equity)}</div></div>
        <div><div style={{ color: C.muted, fontSize: 10, letterSpacing: ".2em" }}>PEAK</div><div className="font-mono text-sm">{money(m.peak_equity)}</div></div>
        <div><div style={{ color: C.muted, fontSize: 10, letterSpacing: ".2em" }}>DROP $</div><div className="font-mono text-sm" style={{ color: m.drawdown_pct > 0 ? c : undefined }}>{money(m.drawdown_dollars)}</div></div>
        <div><div style={{ color: C.muted, fontSize: 10, letterSpacing: ".2em" }}>OPEN</div><div className="font-mono text-sm">{m.open_trades}</div></div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <LiveMeter label="Drop from peak" value={m.drawdown_pct} unit="%" amber={p.thresholds.amber_dd_pct} red={p.thresholds.red_dd_pct} status={p.status} />
        <LiveMeter label="Losing streak" value={m.losing_streak} unit="" amber={p.thresholds.amber_streak} red={p.thresholds.red_streak} status={p.status} />
      </div>
      {/* Open P&L (display-only; never affects the status above). */}
      {livePnl != null && (
        <div data-testid={`risk-open-pnl-${p.setup}`}
             className="rounded px-3 py-2 flex items-center justify-between"
             style={{ background: "rgba(5,14,30,.55)", border: `1px solid ${C.stroke}` }}>
          <span style={{ fontSize: 10, color: C.muted, letterSpacing: ".2em", textTransform: "uppercase" }}>
            Open P&amp;L <span style={{ opacity: .7 }}>(not counted in status)</span>
          </span>
          <span className="font-mono" style={{ fontSize: 13, color: livePnl >= 0 ? C.green : C.red }}>
            {livePnl >= 0 ? "+" : "-"}${Math.abs(Math.round(livePnl)).toLocaleString()}
          </span>
        </div>
      )}
      {/* Money in use now — display only, no alert. */}
      <div data-testid={`risk-money-in-use-${p.setup}`}
           className="rounded px-3 py-2 flex items-center justify-between"
           style={{ background: "rgba(5,14,30,.55)", border: `1px solid ${C.stroke}` }}>
        <span style={{ fontSize: 10, color: C.muted, letterSpacing: ".2em", textTransform: "uppercase" }}>Money in use now</span>
        <span className="font-mono" style={{ fontSize: 13, color: "#e9fbff" }}>
          {money(m.money_in_use_dollars || 0)}
          <span style={{ fontSize: 11, color: C.muted, marginLeft: 8 }}>
            ({num(m.money_in_use_x || 0, 2)}× account)
          </span>
        </span>
      </div>
      <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.5 }}>
        Closed trades: <span className="font-mono" style={{ color: "#e9fbff" }}>{m.closed_trades}</span> of ≈{m.expected_trades} ·
        max streak so far: <span className="font-mono" style={{ color: "#e9fbff" }}>{m.max_losing_streak}</span>
      </div>
    </article>
  );
});

/** Isolated live-polling block. Only this subtree re-renders every 5 min.
 *
 *  Also fetches a lightweight dashboard + live-prices snapshot so each
 *  Setup card can show live Open P&L (display only; never changes the
 *  Risk Lab status). This dual fetch is intentional: the Risk Lab status
 *  stays strictly on CLOSED trades, while Open P&L is purely informational.
 */
function LiveStatusBlock({ onPaper }) {
  const [live, setLive] = useState(null);
  const [openPnl, setOpenPnl] = useState({ s5: null, s6: null });

  // Risk Lab status — 5 minute cadence is plenty, it's CLOSED trades only.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const d = await apiGet("/orb/risk-lab");
        if (!cancelled) { setLive(d); onPaper?.(d); }
      } catch (_) { /* non-blocking */ }
    };
    load();
    const t = setInterval(load, 5 * 60 * 1000);
    return () => { cancelled = true; clearInterval(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Live Open P&L — 60s during market hours, 15min otherwise.
  useEffect(() => {
    let cancelled = false, timer;
    const step = async () => {
      try {
        const d = await apiGet("/orb/dashboard");
        if (cancelled) return;
        const opens = d.open_trades || [];
        const isOpen = !!d.market?.is_open;
        if (opens.length === 0) { setOpenPnl({ s5: 0, s6: 0 }); }
        else {
          const syms = [...new Set(opens.map((t) => String(t.symbol).toUpperCase()))].sort().join(",");
          const px = await apiGet(`/orb/live-prices?symbols=${encodeURIComponent(syms)}`);
          const prices = px.prices || {};
          const acc = { s5: 0, s6: 0 };
          for (const t of opens) {
            const price = prices[String(t.symbol).toUpperCase()]?.price;
            if (price == null || !t.entry || !t.shares) continue;
            const pnl = (price - t.entry) * t.shares;
            acc[t.setup === 6 ? "s6" : "s5"] += pnl;
          }
          setOpenPnl(acc);
        }
        const next = isOpen ? 60_000 : 15 * 60_000;
        if (!cancelled) timer = setTimeout(step, next);
      } catch (_) {
        if (!cancelled) timer = setTimeout(step, 60_000);
      }
    };
    step();
    return () => { cancelled = true; clearTimeout(timer); };
  }, []);

  if (!live?.setups) return null;
  const pnlFor = (setup) => openPnl[setup === 6 ? "s6" : "s5"];
  return (
    <Panel title="LIVE PAPER TEST · FROM 2 NOV 2026" data-testid="live-status">
      <div className="grid gap-4 md:grid-cols-2">
        {live.setups.map((p) => <LiveSetupCard key={p.setup} p={p} livePnl={pnlFor(p.setup)} />)}
      </div>
    </Panel>
  );
}

// ---------- Scale helpers ----------
const linScale = (dom, rng) => (v) => rng[0] + ((v - dom[0]) / (dom[1] - dom[0])) * (rng[1] - rng[0]);
const logScale = (dom, rng) => {
  const [a, b] = [Math.log(Math.max(1, dom[0])), Math.log(Math.max(1, dom[1]))];
  return (v) => rng[0] + ((Math.log(Math.max(1, v)) - a) / (b - a)) * (rng[1] - rng[0]);
};

// ---------- Six metric tiles ----------
function Tile({ label, value, hint, color = C.cyan, testid }) {
  return (
    <div data-testid={testid} className="rounded-lg p-3 flex flex-col gap-1"
         style={{ background: "rgba(5,14,30,.6)", border: `1px solid ${C.stroke}` }}>
      <div style={{ fontSize: 10, color: C.muted, letterSpacing: ".2em", textTransform: "uppercase" }}>{label}</div>
      <div className="font-mono" style={{ fontSize: 20, color }}>{value}</div>
      {hint && <div style={{ fontSize: 10, color: C.muted }}>{hint}</div>}
    </div>
  );
}
const Tiles = memo(function Tiles({ s }) {
  return (
    <div className="grid gap-3 grid-cols-2 md:grid-cols-3 lg:grid-cols-6">
      <Tile label="Profit factor"  value={num(s.pf, 2)}             testid="tile-pf" />
      <Tile label="Net on $10k"    value={money(s.net)}             color={s.net >= 0 ? C.green : C.red} testid="tile-net" />
      <Tile label="Worst drop"     value={pctF(s.maxdd, 1)}         color={C.red} testid="tile-maxdd" />
      <Tile label="Longest losing" value={`${s.streak || 0} losses`} color={C.red} testid="tile-streak" />
      <Tile label="Win rate"       value={pctF(s.win_rate, 1)}      hint={`${s.trades || 0} trades`} testid="tile-winrate" />
      <Tile label="Trades / 2m"    value={num(s.trades_per_2m, 1)}  hint="portfolio pace" testid="tile-pace" />
    </div>
  );
});

function ZoneBox({ color, label, value }) {
  // Allow a smaller font for ranges like "$8,320 – $9,100" so they don't wrap.
  const bigFont = typeof value === "string" && value.length > 10 ? 13 : 16;
  return (
    <div className="rounded p-3" style={{ background: `${color}11`, border: `1px solid ${color}66`, textAlign: "center" }}>
      <div style={{ fontSize: 10, color: C.muted, letterSpacing: ".18em", textTransform: "uppercase" }}>{label}</div>
      <div className="font-mono" style={{ color, fontSize: bigFont, marginTop: 4 }}>{value}</div>
    </div>
  );
}
const ForecastShuffled = memo(function ForecastShuffled({ s }) {
  return (
    <Panel title="2-MONTH FORECAST · SHUFFLED" data-testid="card-shuffled">
      <div className="grid grid-cols-3 gap-2">
        <ZoneBox color={C.red} label="Bad" value={money(s.m2_p05)} />
        <ZoneBox color={C.cyan} label="Typical" value={money(s.m2_p50)} />
        <ZoneBox color={C.green} label="Good" value={money(s.m2_p95)} />
      </div>
      <div style={{ fontSize: 11, color: C.muted, marginTop: 10, lineHeight: 1.5 }}>
        Chance of a losing two months: <span className="font-mono" style={{ color: C.amber }}>{pctF(s.m2_loss, 1)}</span>.
      </div>
    </Panel>
  );
});
const ForecastReal = memo(function ForecastReal({ s }) {
  return (
    <Panel title="2-MONTH FORECAST · REAL HISTORY" data-testid="card-real">
      <div className="grid grid-cols-3 gap-2">
        <ZoneBox color={C.red} label="Bad 1-in-20" value={signPct(s.r2_p05, 1)} />
        <ZoneBox color={C.cyan} label="Typical" value={signPct(s.r2_p50, 1)} />
        <ZoneBox color={C.green} label="Good 1-in-20" value={signPct(s.r2_p95, 1)} />
      </div>
      <div style={{ fontSize: 11, color: C.muted, marginTop: 10, lineHeight: 1.5 }}>
        <div>Chance of a losing two months: <span className="font-mono" style={{ color: C.amber }}>{pctF(s.r2_loss, 1)}</span></div>
        <div>Worst 2 months in backtest: <span className="font-mono" style={{ color: C.red }}>{signPct(s.r2_worst, 1)}</span>
          {s.r2_worst_from && <> ({s.r2_worst_from} → {s.r2_worst_to})</>}</div>
      </div>
    </Panel>
  );
});
const WarningLevels = memo(function WarningLevels({ s, start }) {
  const amberLvl = start * (1 - (s.r2_dd_p50 || 0));
  const redLvl   = start * (1 - (s.r2_dd_p95 || 0));
  return (
    <Panel title="WARNING LEVELS" data-testid="card-warn">
      <div className="grid grid-cols-3 gap-2">
        <ZoneBox color={C.green} label="Green above" value={money(amberLvl)} />
        <ZoneBox color={C.amber} label="Amber zone" value={`${money(redLvl)} \u2013 ${money(amberLvl)}`} />
        <ZoneBox color={C.red}   label="Red below"   value={money(redLvl)} />
      </div>
      <div style={{ fontSize: 11, color: C.muted, marginTop: 10, lineHeight: 1.5 }}>
        Streaks: typical <span className="font-mono" style={{ color: C.amber }}>{s.r2_streak_p50 || 0}</span> losses in a row ·
        1-in-20 stretch <span className="font-mono" style={{ color: C.red }}>{s.r2_streak_p95 || 0}</span> losses in a row.
      </div>
    </Panel>
  );
});

// ---------- BIG FAN CHART (memoised, merged paths) -----------------------
const FanChart = memo(function FanChart({ data2m, dataFull, startEquity, warnDollars, livePaper, scope }) {
  const canShow2m = scope === "ALL" && !!data2m;
  const [view, setView] = useState(canShow2m ? "2m" : "full");
  useEffect(() => { setView(canShow2m ? "2m" : "full"); }, [canShow2m]);
  const d = view === "2m" ? data2m : dataFull;
  const [hover, setHover] = useState(null);

  const W = 1100, H = 320, padL = 60, padR = 20, padT = 20, padB = 30;
  const bands = d?.bands || [];
  const paths = d?.paths || [];
  const n = bands.length || 1;

  // Everything expensive memoised against (d, view, startEquity, livePaper).
  const geometry = useMemo(() => {
    let ys = [];
    for (const b of bands) ys.push(b.p05, b.p95);
    for (const p of paths) for (const v of p) ys.push(v);
    if (livePaper?.length && view === "2m") for (const v of livePaper) ys.push(v);
    if (!ys.length) ys = [startEquity * 0.8, startEquity * 1.2];
    const yAll = [Math.min(...ys), Math.max(...ys)];
    const sx = linScale([0, Math.max(1, n - 1)], [padL, W - padR]);
    const useLog = view === "full";
    const yDom = useLog ? [Math.max(1, yAll[0]), Math.max(2, yAll[1])] : [yAll[0] * 0.98, yAll[1] * 1.02];
    const sy = useLog ? logScale(yDom, [H - padB, padT]) : linScale(yDom, [H - padB, padT]);
    // *** 60 paths → ONE combined "d" string ***
    const combinedPaths = paths.map((p) =>
      p.map((v, j) => `${j === 0 ? "M" : "L"} ${sx(j).toFixed(1)} ${sy(v).toFixed(1)}`).join(" ")
    ).join(" ");
    const bandPath = (loKey, hiKey) => {
      if (!bands.length) return "";
      const up = bands.map((b, i) => `${i === 0 ? "M" : "L"} ${sx(i).toFixed(1)} ${sy(b[hiKey]).toFixed(1)}`).join(" ");
      const down = [...bands].reverse().map((b, i) => `L ${sx(bands.length - 1 - i).toFixed(1)} ${sy(b[loKey]).toFixed(1)}`).join(" ");
      return `${up} ${down} Z`;
    };
    const medianPath = bands.map((b, i) => `${i === 0 ? "M" : "L"} ${sx(i).toFixed(1)} ${sy(b.p50).toFixed(1)}`).join(" ");
    const livePath = (livePaper?.length && view === "2m")
      ? livePaper.map((v, i) => `${i === 0 ? "M" : "L"} ${sx(Math.min(i, n - 1)).toFixed(1)} ${sy(v).toFixed(1)}`).join(" ")
      : "";
    // ticks
    let yTicks;
    if (useLog) {
      const [a, b] = yDom; const t = []; let v = Math.pow(10, Math.floor(Math.log10(a)));
      while (v <= b) { t.push(v); v *= 10; } yTicks = t;
    } else {
      const [a, b] = yDom; const step = Math.pow(10, Math.floor(Math.log10((b - a) / 4)));
      const t = []; for (let v = Math.ceil(a / step) * step; v < b; v += step) t.push(v);
      yTicks = t;
    }
    return { sx, sy, combinedPaths, bandPath05_95: bandPath("p05", "p95"), bandPath25_75: bandPath("p25", "p75"), medianPath, livePath, yTicks };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [d, view, startEquity, livePaper, n]);

  const { sx, sy, combinedPaths, bandPath05_95, bandPath25_75, medianPath, livePath, yTicks } = geometry;

  const onMove = (e) => {
    const svg = e.currentTarget;
    const rect = svg.getBoundingClientRect();
    const px = ((e.clientX - rect.left) / rect.width) * W;
    const i = Math.max(0, Math.min(bands.length - 1, Math.round((px - padL) / (W - padR - padL) * (bands.length - 1))));
    if (i !== hover) setHover(i);
  };
  const hoverBand = hover != null ? bands[hover] : null;
  const hoverPaper = (hover != null && livePaper && view === "2m") ? livePaper[hover] : null;

  return (
    <Panel
      data-testid="fan-chart"
      title={<span>EQUITY PATHS · {view === "2m" ? "NEXT 2 MONTHS" : "FULL HISTORY"}</span>}
      right={
        <div className="flex gap-1">
          <ToggleBtn active={view === "2m"} disabled={!canShow2m} onClick={() => setView("2m")} testid="fan-2m">Next 2 months</ToggleBtn>
          <ToggleBtn active={view === "full"} onClick={() => setView("full")} testid="fan-full">Full history</ToggleBtn>
        </div>
      }
    >
      <div className="relative" style={{ minHeight: 340 }}>
        <svg viewBox={`0 0 ${W} ${H}`} width="100%" onMouseMove={onMove} onMouseLeave={() => setHover(null)}>
          {yTicks.map((v, i) => (
            <g key={i}>
              <line x1={padL} x2={W - padR} y1={sy(v)} y2={sy(v)} stroke="rgba(255,255,255,.06)" />
              <text x={padL - 6} y={sy(v) + 3} fontSize="9" fill={C.muted} textAnchor="end" fontFamily="JetBrains Mono">
                {v >= 1000 ? `$${(v / 1000).toFixed(v >= 10000 ? 0 : 1)}k` : `$${v.toFixed(0)}`}
              </text>
            </g>
          ))}
          {/* ONE combined path for all 60 simulated paths */}
          <path d={combinedPaths} fill="none" stroke="#5f7fa0" strokeOpacity="0.18" strokeWidth="0.8" />
          <path d={bandPath05_95} fill={C.cyan} fillOpacity="0.12" />
          <path d={bandPath25_75} fill={C.cyan} fillOpacity="0.24" />
          {/* glow retained here per spec */}
          <path d={medianPath} fill="none" stroke={C.cyan} strokeWidth="2" style={{ filter: `drop-shadow(0 0 3px ${C.cyan})` }} />
          <line x1={padL} x2={W - padR} y1={sy(startEquity)} y2={sy(startEquity)} stroke="#ffffff88" strokeDasharray="3 3" />
          <text x={W - padR - 4} y={sy(startEquity) - 3} fontSize="9" fill="#ffffffaa" textAnchor="end">${startEquity.toLocaleString()}</text>
          {view === "2m" && warnDollars && (
            <>
              <line x1={padL} x2={W - padR} y1={sy(warnDollars.amber)} y2={sy(warnDollars.amber)} stroke={C.amber} strokeDasharray="5 4" />
              <text x={padL + 6} y={sy(warnDollars.amber) - 3} fontSize="9" fill={C.amber}>amber {money(warnDollars.amber)}</text>
              <line x1={padL} x2={W - padR} y1={sy(warnDollars.red)}   y2={sy(warnDollars.red)}   stroke={C.red}   strokeDasharray="5 4" />
              <text x={padL + 6} y={sy(warnDollars.red) - 3} fontSize="9" fill={C.red}>red {money(warnDollars.red)}</text>
            </>
          )}
          {livePath && (
            <>
              <path d={livePath} fill="none" stroke={C.pink} strokeWidth="1.8" />
              {livePaper.map((v, i) => (
                <circle key={i} cx={sx(Math.min(i, n - 1))} cy={sy(v)} r="2.5" fill={C.pink} />
              ))}
            </>
          )}
          {hover != null && (
            <>
              <line x1={sx(hover)} x2={sx(hover)} y1={padT} y2={H - padB} stroke="#ffffff44" />
              {hoverBand && (
                <>
                  <circle cx={sx(hover)} cy={sy(hoverBand.p50)} r="3" fill={C.cyan} />
                  <circle cx={sx(hover)} cy={sy(hoverBand.p05)} r="2" fill={C.red} opacity="0.8" />
                  <circle cx={sx(hover)} cy={sy(hoverBand.p95)} r="2" fill={C.green} opacity="0.8" />
                </>
              )}
            </>
          )}
          <text x={padL} y={H - 10} fontSize="9" fill={C.muted}>trade 1</text>
          <text x={W - padR} y={H - 10} fontSize="9" fill={C.muted} textAnchor="end">trade {n - 1}</text>
        </svg>
        {hover != null && hoverBand && (
          <div className="pointer-events-none" style={{
            position: "absolute", top: 8, right: 8,
            background: "rgba(5,14,30,.9)", border: `1px solid ${C.stroke}`, padding: 10,
            fontSize: 11, color: "#e9fbff", borderRadius: 6, minWidth: 180,
          }}>
            <div style={{ color: C.muted, letterSpacing: ".2em", fontSize: 9 }}>AFTER {hover} TRADES</div>
            <div className="font-mono mt-1">
              <div>95% <span style={{ color: C.green }}>{money(hoverBand.p95)}</span></div>
              <div>75% <span>{money(hoverBand.p75)}</span></div>
              <div>50% <span style={{ color: C.cyan }}>{money(hoverBand.p50)}</span></div>
              <div>25% <span>{money(hoverBand.p25)}</span></div>
              <div>5% <span style={{ color: C.red }}>{money(hoverBand.p05)}</span></div>
              {hoverPaper != null && <div>paper <span style={{ color: C.pink }}>{money(hoverPaper)}</span></div>}
            </div>
          </div>
        )}
      </div>
    </Panel>
  );
});
function ToggleBtn({ active, disabled, onClick, children, testid }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} data-testid={testid}
      className="ora-navlink"
      style={{
        opacity: disabled ? 0.4 : 1,
        cursor: disabled ? "not-allowed" : "pointer",
        borderColor: active ? C.cyan : undefined,
      }}>
      {children}
    </button>
  );
}

// ---------- Memoised secondary charts ----------
const Histogram = memo(function Histogram({ title, bins, p50, p95, baseColor, formatBin, testid }) {
  const W = 540, H = 180, padL = 40, padR = 10, padT = 20, padB = 30;
  const g = useMemo(() => {
    if (!bins?.length) return null;
    const maxCount = Math.max(...bins.map((b) => b[2]), 1);
    const binW = (W - padL - padR) / bins.length;
    const sy = linScale([0, maxCount], [H - padB, padT]);
    const binLo = bins[0][0], binHi = bins[bins.length - 1][1];
    const xForVal = (v) => padL + ((v - binLo) / (binHi - binLo)) * (W - padL - padR);
    return { maxCount, binW, sy, binLo, binHi, xForVal };
  }, [bins]);
  if (!bins?.length || !g) return null;
  const colorForBin = (lo, hi) => {
    const mid = (lo + hi) / 2;
    if (mid >= p95) return C.red;
    if (mid >= p50) return C.amber;
    return baseColor;
  };
  return (
    <Panel title={title} data-testid={testid}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%">
        {bins.map((b, i) => {
          const [lo, hi, cnt] = b;
          const y = g.sy(cnt);
          return <rect key={i} x={padL + i * g.binW + 1} width={g.binW - 1.5} y={y} height={H - padB - y} fill={colorForBin(lo, hi)} opacity="0.75" />;
        })}
        <line x1={g.xForVal(p50)} x2={g.xForVal(p50)} y1={padT} y2={H - padB} stroke={C.cyan} strokeDasharray="3 3" />
        <text x={g.xForVal(p50)} y={padT - 4} fontSize="9" fill={C.cyan} textAnchor="middle">typical {formatBin(p50)}</text>
        <line x1={g.xForVal(p95)} x2={g.xForVal(p95)} y1={padT} y2={H - padB} stroke={C.red} strokeDasharray="3 3" />
        <text x={g.xForVal(p95)} y={padT - 4} fontSize="9" fill={C.red} textAnchor="middle">1 in 20 {formatBin(p95)}</text>
        <text x={padL}     y={H - 10} fontSize="9" fill={C.muted}>{formatBin(g.binLo)}</text>
        <text x={W - padR} y={H - 10} fontSize="9" fill={C.muted} textAnchor="end">{formatBin(g.binHi)}</text>
      </svg>
    </Panel>
  );
});

const Real2mChart = memo(function Real2mChart({ bins, worst, typical, testid }) {
  const W = 540, H = 180, padL = 40, padR = 10, padT = 20, padB = 30;
  const g = useMemo(() => {
    if (!bins?.length) return null;
    const maxCount = Math.max(...bins.map((b) => b[2]), 1);
    const binW = (W - padL - padR) / bins.length;
    const sy = linScale([0, maxCount], [H - padB, padT]);
    const binLo = bins[0][0], binHi = bins[bins.length - 1][1];
    const xForVal = (v) => padL + ((v - binLo) / (binHi - binLo)) * (W - padL - padR);
    return { maxCount, binW, sy, binLo, binHi, xForVal };
  }, [bins]);
  if (!bins?.length || !g) return null;
  return (
    <Panel title="REAL 2-MONTH RETURNS" data-testid={testid}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%">
        {bins.map((b, i) => {
          const [lo, hi, cnt] = b;
          const y = g.sy(cnt); const mid = (lo + hi) / 2; const fill = mid < 0 ? C.red : C.green;
          return <rect key={i} x={padL + i * g.binW + 1} width={g.binW - 1.5} y={y} height={H - padB - y} fill={fill} opacity="0.72" />;
        })}
        {g.binLo < 0 && g.binHi > 0 && <line x1={g.xForVal(0)} x2={g.xForVal(0)} y1={padT} y2={H - padB} stroke="#ffffff55" />}
        {typical != null && (
          <>
            <line x1={g.xForVal(typical)} x2={g.xForVal(typical)} y1={padT} y2={H - padB} stroke={C.cyan} strokeDasharray="3 3" />
            <text x={g.xForVal(typical)} y={padT - 4} fontSize="9" fill={C.cyan} textAnchor="middle">typical {signPct(typical, 0)}</text>
          </>
        )}
        {worst != null && (
          <>
            <line x1={g.xForVal(worst)} x2={g.xForVal(worst)} y1={padT} y2={H - padB} stroke={C.red} strokeDasharray="3 3" />
            <text x={g.xForVal(worst)} y={padT - 4} fontSize="9" fill={C.red} textAnchor="middle">worst {signPct(worst, 0)}</text>
          </>
        )}
        <text x={padL}     y={H - 10} fontSize="9" fill={C.muted}>{signPct(g.binLo, 0)}</text>
        <text x={W - padR} y={H - 10} fontSize="9" fill={C.muted} textAnchor="end">{signPct(g.binHi, 0)}</text>
      </svg>
    </Panel>
  );
});

const YearsChart = memo(function YearsChart({ years, testid }) {
  const entries = useMemo(() => Object.entries(years || {}).sort(([a], [b]) => Number(a) - Number(b)), [years]);
  if (!entries.length) return null;
  const W = 540, H = 180, padL = 40, padR = 10, padT = 20, padB = 36;
  const vals = entries.map((e) => e[1]);
  const minV = Math.min(0, ...vals), maxV = Math.max(0, ...vals);
  const sy = linScale([minV, maxV], [H - padB, padT]);
  const binW = (W - padL - padR) / entries.length;
  const y0 = sy(0);
  return (
    <Panel title="PROFIT BY YEAR" data-testid={testid}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%">
        <line x1={padL} x2={W - padR} y1={y0} y2={y0} stroke="#ffffff33" />
        {entries.map(([year, v], i) => {
          const yEnd = sy(v); const h = Math.abs(y0 - yEnd); const fill = v >= 0 ? C.green : C.red;
          return (
            <g key={year}>
              <rect x={padL + i * binW + 4} y={Math.min(y0, yEnd)} width={binW - 8} height={h} fill={fill} opacity="0.78" />
              <text x={padL + i * binW + binW / 2} y={H - 18} fontSize="9" fill={C.muted} textAnchor="middle">{year}</text>
              <text x={padL + i * binW + binW / 2} y={v >= 0 ? yEnd - 4 : yEnd + 10} fontSize="9" fill={fill} textAnchor="middle">
                {v >= 0 ? "+" : ""}${(v / 1000).toFixed(1)}k
              </text>
            </g>
          );
        })}
      </svg>
    </Panel>
  );
});

const EquityCurve = memo(function EquityCurve({ curve, startEquity, testid }) {
  const W = 1100, H = 220, padL = 60, padR = 20, padT = 20, padB = 32;
  const g = useMemo(() => {
    if (!curve?.length) return null;
    const vals = curve.map((r) => r[1]);
    const yDom = [Math.min(startEquity, ...vals) * 0.95, Math.max(startEquity, ...vals) * 1.05];
    const sx = linScale([0, curve.length - 1], [padL, W - padR]);
    const sy = linScale(yDom, [H - padB, padT]);
    const path = curve.map((r, i) => `${i === 0 ? "M" : "L"} ${sx(i).toFixed(1)} ${sy(r[1]).toFixed(1)}`).join(" ");
    const area = `${path} L ${sx(curve.length - 1).toFixed(1)} ${sy(yDom[0]).toFixed(1)} L ${sx(0).toFixed(1)} ${sy(yDom[0]).toFixed(1)} Z`;
    const tickIdx = [0, Math.floor(curve.length / 4), Math.floor(curve.length / 2), Math.floor(3 * curve.length / 4), curve.length - 1];
    return { sx, sy, path, area, tickIdx };
  }, [curve, startEquity]);
  if (!curve?.length || !g) return null;
  const last = curve[curve.length - 1];
  return (
    <Panel title="REAL BACKTEST EQUITY" data-testid={testid}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%">
        <defs>
          <linearGradient id="rl-eq-fill" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor={C.purple} stopOpacity="0.5" />
            <stop offset="100%" stopColor={C.purple} stopOpacity="0" />
          </linearGradient>
        </defs>
        <line x1={padL} x2={W - padR} y1={g.sy(startEquity)} y2={g.sy(startEquity)} stroke="#ffffff55" strokeDasharray="3 3" />
        <text x={padL + 4} y={g.sy(startEquity) - 3} fontSize="9" fill="#ffffff99">${startEquity.toLocaleString()}</text>
        <path d={g.area} fill="url(#rl-eq-fill)" />
        <path d={g.path} fill="none" stroke={C.purple} strokeWidth="1.8" />
        <circle cx={g.sx(curve.length - 1)} cy={g.sy(last[1])} r="3" fill={C.purple} />
        <text x={g.sx(curve.length - 1) - 6} y={g.sy(last[1]) - 8} fontSize="10" fill={C.purple} textAnchor="end">
          ${last[1].toLocaleString()}
        </text>
        {g.tickIdx.map((i) => (
          <text key={i} x={g.sx(i)} y={H - 10} fontSize="9" fill={C.muted} textAnchor={i === 0 ? "start" : i === curve.length - 1 ? "end" : "middle"}>
            {String(curve[i][0]).slice(0, 7)}
          </text>
        ))}
      </svg>
    </Panel>
  );
});

const StockTable = memo(function StockTable({ setupData, currentSetup, onPick, testid }) {
  const rows = useMemo(() =>
    Object.entries(setupData || {})
      .filter(([k]) => k !== "ALL")
      .map(([sym, data]) => ({ sym, ...data.summary }))
      .sort((a, b) => (b.pf || 0) - (a.pf || 0)),
    [setupData]
  );
  if (!rows.length) return null;
  const maxPF = Math.max(...rows.map((r) => r.pf || 0), 1);
  return (
    <Panel title={`STOCKS · SETUP ${currentSetup === "S5" ? 5 : 6}`} data-testid={testid}>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
          <thead>
            <tr style={{ color: C.muted }}>
              {["Stock", "PF", "Net", "Win %", "Worst drop", "Drop 1-in-20", "Trades", "In portfolio"].map((h) => (
                <th key={h} style={{ padding: "6px 10px", textAlign: ["Stock", "In portfolio"].includes(h) ? "left" : "right",
                    letterSpacing: ".2em", textTransform: "uppercase", fontSize: 10, borderBottom: `1px solid ${C.stroke}` }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.sym} data-testid={`stock-row-${r.sym}`} onClick={() => onPick(r.sym)}
                  style={{ cursor: "pointer", borderBottom: "1px solid rgba(255,255,255,.04)" }}>
                <td style={{ padding: "6px 10px", fontFamily: "JetBrains Mono" }}>{r.sym}</td>
                <td style={{ padding: "6px 10px", textAlign: "right" }}>
                  <div className="flex items-center justify-end gap-2">
                    <span className="font-mono">{num(r.pf, 2)}</span>
                    <span style={{ display: "inline-block", width: 50, height: 5, background: "rgba(255,255,255,.08)", borderRadius: 999, position: "relative" }}>
                      <span style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: `${Math.min(100, (r.pf / maxPF) * 100)}%`, background: C.cyan, borderRadius: 999 }} />
                    </span>
                  </div>
                </td>
                <td style={{ padding: "6px 10px", textAlign: "right", fontFamily: "JetBrains Mono", color: r.net >= 0 ? C.green : C.red }}>{money(r.net)}</td>
                <td style={{ padding: "6px 10px", textAlign: "right", fontFamily: "JetBrains Mono" }}>{pctF(r.win_rate, 1)}</td>
                <td style={{ padding: "6px 10px", textAlign: "right", fontFamily: "JetBrains Mono", color: C.red }}>{pctF(r.maxdd, 1)}</td>
                <td style={{ padding: "6px 10px", textAlign: "right", fontFamily: "JetBrains Mono", color: C.amber }}>{pctF(r.mc_dd_p95, 1)}</td>
                <td style={{ padding: "6px 10px", textAlign: "right", fontFamily: "JetBrains Mono" }}>{r.trades}</td>
                <td style={{ padding: "6px 10px" }}>
                  {r.in_portfolio ? <span style={{ color: C.green }}>● yes</span> : <span style={{ color: C.muted }}>— no</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  );
});

const NoteCards = memo(function NoteCards({ s }) {
  const signals = s.signals || 0;
  const skipped = s.skipped || 0;
  const peakX   = s.max_exposure || 0;
  return (
    <div className="grid gap-4 md:grid-cols-3">
      <Panel title="MONEY IN USE" data-testid="note-money-in-use">
        <div style={{ fontSize: 12, color: "#cfeaff", lineHeight: 1.6 }}>
          Signals: <span className="font-mono" style={{ color: C.cyan }}>{signals.toLocaleString?.() || 0}</span><br/>
          Skipped: <span className="font-mono" style={{ color: C.cyan }}>{skipped.toLocaleString?.() || 0}</span><br/>
          Peak money in use: <span className="font-mono" style={{ color: C.amber }}>{num(peakX, 2)}×</span>
        </div>
        <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.5, marginTop: 10 }}>
          Fine on paper. A live account needs a limit on open trades.
        </div>
      </Panel>
      <Panel title="LEFT OUT · LIN AND QQQM" data-testid="note-left-out">
        <div style={{ fontSize: 12, color: "#cfeaff", lineHeight: 1.6 }}>
          Without LIN and QQQM the backtest shows a profit factor of
          <span className="font-mono" style={{ color: C.cyan }}> {num(s.w17_pf, 2)}</span> and a net of
          <span className="font-mono" style={{ color: s.w17_net >= 0 ? C.green : C.red }}> {money(s.w17_net)}</span>.
          Compare to the full set: PF <span className="font-mono" style={{ color: C.cyan }}>{num(s.pf, 2)}</span>, net
          <span className="font-mono" style={{ color: s.net >= 0 ? C.green : C.red }}> {money(s.net)}</span>.
        </div>
      </Panel>
      <Panel title="HOW TO READ THIS" data-testid="note-howto">
        <div style={{ fontSize: 12, color: "#cfeaff", lineHeight: 1.6 }}>
          Shuffled forecasts replay every past trade in random order to show what could happen next.
          Real-history forecasts use actual two-month windows from the backtest, so they already carry the real-world streaks and shocks.
          When they disagree, trust the real history.
        </div>
      </Panel>
    </div>
  );
});

// ---------- Main page ----------
export default function RiskLabPage() {
  const { logout } = useAuth();
  const nav = useNavigate();

  const [bt, setBt] = useState(_btCache);           // cached across visits
  const [err, setErr] = useState("");
  const [setupKey, setSetupKey] = useState("S5");
  const [scope, setScope] = useState("ALL");
  const [livePaper, setLivePaper] = useState(null);

  // Toggle a body class so CSS can pause every background animation when
  // the tab is hidden. Scoped to this page.
  useEffect(() => {
    const onVis = () => document.body.classList.toggle("ora-tab-hidden", document.hidden);
    onVis(); document.addEventListener("visibilitychange", onVis);
    return () => { document.removeEventListener("visibilitychange", onVis); document.body.classList.remove("ora-tab-hidden"); };
  }, []);

  useEffect(() => {
    if (_btCache) return;
    (async () => {
      try { const b = await apiGet("/orb/risk-lab/backtest"); _btCache = b; setBt(b); }
      catch (e) { setErr(e.message || "Failed to load Risk Lab"); }
    })();
  }, []);

  const scopeOptions = useMemo(() => {
    if (!bt) return [];
    const allSyms = Object.keys(bt.setups?.[setupKey] || {}).filter((k) => k !== "ALL").sort();
    // LIN and QQQM are reference-only comparisons; the "ALL" combined
    // portfolio in the new 16-stock backtest excludes them.
    const REF_ONLY = new Set(["LIN", "QQQM"]);
    const portfolioSyms = allSyms.filter((s) => !REF_ONLY.has(s));
    const refSyms = allSyms.filter((s) => REF_ONLY.has(s));
    // Prefer the explicit `stocks` field from the artifact so the label
    // always matches what the backtest actually ran on.
    const count = bt.stocks || portfolioSyms.length;
    return [
      { v: "ALL", label: `Combined portfolio · ${count} stocks` },
      ...portfolioSyms.map((s) => ({ v: s, label: s })),
      ...refSyms.map((s) => ({ v: s, label: `${s} · reference only` })),
    ];
  }, [bt, setupKey]);
  useEffect(() => {
    if (bt && scope !== "ALL" && !bt.setups?.[setupKey]?.[scope]) setScope("ALL");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setupKey]);

  const start = bt?.start_equity || 10000;
  const data = bt?.setups?.[setupKey]?.[scope];
  const s = data?.summary || {};
  const scopeIsAll = scope === "ALL";

  // Compute the pink paper line from the live payload (only when the setup/scope changes).
  const paper = useMemo(() => {
    if (!livePaper || !scopeIsAll) return null;
    const nn = setupKey === "S5" ? 5 : 6;
    const setup = livePaper.setups?.find((x) => x.setup === nn);
    const pts = setup?.metrics?.curve || [];
    if (!pts.length) return null;
    return [setup.metrics.start_balance, ...pts.map((p) => p.equity)];
  }, [livePaper, setupKey, scopeIsAll]);

  const warnDollars = useMemo(() => s ? ({
    amber: start * (1 - (s.r2_dd_p50 || 0)),
    red:   start * (1 - (s.r2_dd_p95 || 0)),
  }) : null, [s, start]);

  const headerSentence = useMemo(() => {
    if (!s) return "";
    return `A normal two months dips about ${pctF(s.r2_dd_p50, 1)} with ${s.r2_streak_p50 || 0} losses in a row. ` +
      `One stretch in twenty dips ${pctF(s.r2_dd_p95, 1)} with ${s.r2_streak_p95 || 0} losses. Past that, stop and review.`;
  }, [s]);

  return (
    <div className="oracle-root" data-testid="risk-lab-page">
      {/* Lightweight static starfield only (no animated nebula/comets on this page) */}
      <div className="stars" />
      <div className="p-4 md:p-6 flex flex-col gap-4">
        <OracleNav onLogout={() => { logout?.(); nav("/login"); }} />

        <Panel data-testid="risk-lab-header" pad={4}>
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <div className="ora-h" style={{ fontSize: 20, letterSpacing: ".3em" }}>ORACLE RISK LAB</div>
              {s && (
                <div style={{ fontSize: 13, color: "#cfeaff", marginTop: 6, maxWidth: 820, lineHeight: 1.6 }}>
                  {headerSentence}
                </div>
              )}
            </div>
            <div className="flex items-center gap-3 flex-wrap" data-testid="risk-lab-controls">
              <div className="flex items-center gap-1 rounded" style={{ border: `1px solid ${C.stroke}`, padding: 3 }}>
                {["S5", "S6"].map((k) => (
                  <button key={k} onClick={() => setSetupKey(k)}
                          data-testid={`setup-toggle-${k.toLowerCase()}`}
                          className="ora-navlink" style={{
                            borderColor: setupKey === k ? C.cyan : "transparent",
                            padding: "4px 12px",
                          }}>Setup {k === "S5" ? 5 : 6}</button>
                ))}
              </div>
              <select value={scope} onChange={(e) => setScope(e.target.value)}
                      data-testid="scope-select"
                      className="ora-navlink"
                      style={{ padding: "6px 10px", background: "rgba(5,14,30,.8)", color: "#e9fbff" }}>
                {scopeOptions.map((o) => (
                  <option key={o.v} value={o.v} style={{ background: "#0a1424" }}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>
        </Panel>

        {err && <Panel data-testid="risk-lab-error"><div className="text-red-400 text-sm">{err}</div></Panel>}

        {/* Live status is its OWN component with its own poll — chart tree never re-renders on polls. */}
        <LiveStatusBlock onPaper={setLivePaper} />

        {!bt && !err && <Panel><div style={{ fontSize: 12, color: C.muted }}>Loading backtest data…</div></Panel>}

        {data && (
          <>
            {/* Section banner for the backtest numbers below. */}
            <div className="flex items-center gap-3" data-testid="section-backtest">
              <span className="ora-h" style={{ fontSize: 12, letterSpacing: ".3em", color: C.cyan }}>
                BACKTEST 2020–2026
              </span>
              <span style={{ flex: 1, height: 1, background: `linear-gradient(90deg, ${C.stroke}, transparent)` }} />
            </div>

            {/* Reference note — fixed copy per spec. Shown once above the tiles. */}
            {scopeIsAll && (
              <Panel data-testid="reference-note" pad={3}>
                <div style={{ fontSize: 12, color: "#cfeaff", lineHeight: 1.6 }}>
                  Typical 2-month result: Setup 5 <span className="font-mono" style={{ color: C.green }}>+16%</span>,
                  Setup 6 <span className="font-mono" style={{ color: C.green }}>+14%</span>.
                  About <span className="font-mono" style={{ color: C.amber }}>1 in 3</span> two-month periods end below
                  <span className="font-mono" style={{ color: "#e9fbff" }}> $10,000</span> even when the strategy works.
                  Every signal is taken, so open positions can total more than the account (up to
                  <span className="font-mono" style={{ color: C.amber }}> 5×</span> in the backtest).
                </div>
              </Panel>
            )}

            <Tiles s={s} />

            {scopeIsAll && (
              <div className="grid gap-4 md:grid-cols-3">
                <ForecastShuffled s={s} />
                <ForecastReal s={s} />
                <WarningLevels s={s} start={start} />
              </div>
            )}

            {/* Fan chart & everything below is lazy-rendered to speed first paint. */}
            <Lazy minHeight={360}>
              <FanChart
                data2m={scopeIsAll ? data.mc_2m : null}
                dataFull={data.mc_full}
                startEquity={start}
                warnDollars={warnDollars}
                livePaper={paper}
                scope={scope}
              />
            </Lazy>

            <Lazy minHeight={220}>
              {(data.mc_2m || data.mc_full) && (
                <div className="grid gap-4 md:grid-cols-2">
                  <Histogram
                    title="HOW DEEP THE DIPS GO"
                    bins={(scopeIsAll ? data.mc_2m : data.mc_full)?.hist?.dd}
                    p50={scopeIsAll ? s.m2_dd_p50 ?? s.r2_dd_p50 : s.mc_dd_p50}
                    p95={scopeIsAll ? s.m2_dd_p95 ?? s.r2_dd_p95 : s.mc_dd_p95}
                    baseColor={C.cyan}
                    formatBin={(v) => `${(v * 100).toFixed(0)}%`}
                    testid="hist-dd"
                  />
                  <Histogram
                    title="LOSING STREAKS"
                    bins={(scopeIsAll ? data.mc_2m : data.mc_full)?.hist?.streak}
                    p50={scopeIsAll ? s.m2_streak_p50 ?? s.r2_streak_p50 : s.mc_streak_p50}
                    p95={scopeIsAll ? s.m2_streak_p95 ?? s.r2_streak_p95 : s.mc_streak_p95}
                    baseColor={C.purple}
                    formatBin={(v) => `${Math.round(v)}`}
                    testid="hist-streak"
                  />
                </div>
              )}
            </Lazy>

            {scopeIsAll && (
              <Lazy minHeight={220}>
                <div className="grid gap-4 md:grid-cols-2">
                  <Real2mChart bins={data.real2m_returns_hist} worst={s.r2_worst} typical={s.r2_p50} testid="chart-real2m" />
                  <YearsChart years={data.years} testid="chart-years" />
                </div>
              </Lazy>
            )}

            {scopeIsAll && data.curve?.length > 0 && (
              <Lazy minHeight={260}>
                <EquityCurve curve={data.curve} startEquity={start} testid="chart-equity" />
              </Lazy>
            )}

            <Lazy minHeight={300}>
              <StockTable
                setupData={bt?.setups?.[setupKey]}
                currentSetup={setupKey}
                onPick={(sym) => setScope(sym)}
                testid="stock-table"
              />
            </Lazy>

            {scopeIsAll && (
              <Lazy minHeight={180}>
                <NoteCards s={s} />
              </Lazy>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// --- Thin status strip (used on the Oracle Trades page) ---------------------
export function RiskStatusStrip({ openPnl }) {
  const [data, setData] = useState(null);
  const nav = useNavigate();
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try { const d = await apiGet("/orb/risk-lab"); if (!cancelled) setData(d); }
      catch (_) { /* non-blocking */ }
    };
    load();
    const t = setInterval(load, 5 * 60 * 1000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);
  if (!data) return null;
  const s5 = data.setups?.find((x) => x.setup === 5);
  const s6 = data.setups?.find((x) => x.setup === 6);
  if (!s5 || !s6) return null;
  const worst = [s5.status, s6.status].includes("red")
    ? "red"
    : [s5.status, s6.status].includes("amber")
      ? "amber" : "green";
  const borderCol = RISK_COLOR[worst];
  const glow = worst === "green" ? "none" : `0 0 10px ${borderCol}aa`;
  const pnlNum = (v) => {
    if (v == null || Number.isNaN(v)) return null;
    const sign = v >= 0 ? "+" : "-";
    const col  = v >= 0 ? C.green : C.red;
    return <span className="font-mono" style={{ color: col }}>{sign}${Math.abs(Math.round(v)).toLocaleString()}</span>;
  };
  return (
    <button type="button" data-testid="risk-status-strip" onClick={() => nav("/risk-lab")}
            className="w-full flex items-center justify-between gap-4 mb-4 px-4 py-2 rounded"
            style={{ background: "rgba(5,14,30,.65)", border: `1px solid ${borderCol}aa`, cursor: "pointer", textAlign: "left", boxShadow: glow }}
            title="Open Risk Lab">
      <span className="ora-h" style={{ fontSize: 11 }}>RISK LAB</span>
      <span className="flex items-center gap-5 flex-wrap">
        {[s5, s6].map((p) => {
          const c = RISK_COLOR[p.status];
          const key = p.setup === 6 ? "s6" : "s5";
          const livePnl = openPnl?.[key];
          return (
            <span key={p.setup} className="flex items-center gap-2" data-testid={`strip-setup-${p.setup}`}>
              <span style={{ fontSize: 10, color: C.muted, letterSpacing: ".2em" }}>S{p.setup}</span>
              <span style={{ width: 8, height: 8, borderRadius: 999, background: c, boxShadow: `0 0 6px ${c}` }} />
              <span className="ora-h" style={{ fontSize: 11, color: c }}>{p.status.toUpperCase()}</span>
              {livePnl != null && (
                <>
                  <span style={{ fontSize: 10, color: C.muted }}>·</span>
                  <span style={{ fontSize: 10, color: C.muted, letterSpacing: ".2em" }}>P&amp;L</span>
                  {pnlNum(livePnl)}
                </>
              )}
            </span>
          );
        })}
      </span>
      <span style={{ fontSize: 10, color: C.muted, letterSpacing: ".2em" }}>OPEN →</span>
    </button>
  );
}
